import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface NavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: React.ReactNode;
  /** Match exactly. Default true for `/`, false otherwise (prefix match). */
  exact?: boolean;
  collapsed?: boolean;
  onClick?: () => void;
}

/**
 * Sidebar nav row — Linear-style. Hairline focus, subtle active state, no shadow.
 */
export const NavItem = React.forwardRef<HTMLAnchorElement, NavItemProps>(
  ({ to, icon: Icon, label, badge, exact, collapsed, onClick }, ref) => {
    const { pathname, search } = useLocation();

    // Split `to` into pathname + query so a single nav item can target a
    // specific section of a single-route page (e.g. /admin/dashboard?section=revenue).
    const qIdx = to.indexOf("?");
    const toPath = qIdx === -1 ? to : to.slice(0, qIdx);
    const toQuery = qIdx === -1 ? "" : to.slice(qIdx + 1);

    const isExact = exact ?? toPath === "/";
    const pathMatches = isExact
      ? pathname === toPath
      : pathname === toPath || pathname.startsWith(toPath + "/");

    // When `to` includes a query string, every key in it must match the
    // current URL for the item to be considered active. Other query keys
    // present on the current URL are ignored so deep links don’t break
    // active-state highlighting.
    let queryMatches = true;
    if (toQuery) {
      const want = new URLSearchParams(toQuery);
      const have = new URLSearchParams(search);
      for (const [k, v] of want.entries()) {
        if (have.get(k) !== v) {
          queryMatches = false;
          break;
        }
      }
    } else if (pathMatches && pathname === toPath) {
      // Items without an explicit `?section=` should only be active when
      // the URL has no `section` either — prevents “Command center” from
      // staying highlighted while you’re on `?section=revenue`.
      const have = new URLSearchParams(search);
      if (have.has("section")) queryMatches = false;
    }

    const active = pathMatches && queryMatches;

    return (
      <Link
        ref={ref}
        to={to}
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        title={collapsed ? label : undefined}
        className={cn(
          "group relative flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] font-medium leading-5 transition-colors duration-150",
          "text-[hsl(var(--rd-text-2))] hover:text-[hsl(var(--rd-text-1))] hover:bg-[hsl(var(--rd-surface-2))]",
          active && "bg-[hsl(var(--rd-surface-2))] text-[hsl(var(--rd-text-1))]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--rd-accent-500))] focus-visible:ring-offset-0",
          collapsed && "justify-center px-1.5"
        )}
      >
        {/* Active indicator — left rail */}
        {active && (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-[hsl(var(--rd-accent-500))]"
          />
        )}
        <Icon
          className={cn(
            "h-4 w-4 shrink-0 transition-colors",
            active ? "text-[hsl(var(--rd-text-1))]" : "text-[hsl(var(--rd-text-3))] group-hover:text-[hsl(var(--rd-text-2))]"
          )}
        />
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{label}</span>
            {badge}
          </>
        )}
      </Link>
    );
  }
);
NavItem.displayName = "NavItem";
